import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from '../components/ResponsiveDataTable';
import { ShipmentDriverCell, ShipmentOrdersCell, ShipmentVehicleTypeCell } from '../components/ShipmentListCells';
import { assignShipmentDriver, getShipments, moveShipmentOrder, updateShipmentVehicleType } from '../config/api';
import './Shipments.css';

const STATUS_LABELS = { pending: 'Pending', started: 'Started', completed: 'Completed' };
const SHIPMENT_STATUS_LABELS = { pending: 'Ready to start', started: 'Ongoing', completed: 'Completed' };
const VEHICLE_LABELS = { twoWheeler: 'Two-wheeler', fourWheeler: 'Four-wheeler' };
const EMPTY_SUMMARY = { shipments: 0, orders: 0, pending: 0, started: 0, completed: 0 };

const getCurrentShipmentDate = () => {
  const dateParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date()).reduce((parts, part) => ({ ...parts, [part.type]: part.value }), {});
  return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
};

const SearchIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4 4" /></svg>;
const ViewIcon = () => <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 10h11M11 6l4 4-4 4" /></svg>;

const getCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem('userData_30jan') || '{}'); }
  catch (_) { return {}; }
};

const formatDate = (value) => value ? new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric',
}) : '—';

const formatDateTime = (value) => value ? new Date(value).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : null;

const ShipmentStartedAt = ({ value }) => {
  if (!value) return <span className="shipment-not-started">—</span>;
  const date = new Date(value);
  return <div className="shipment-started-at"><strong>{date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}</strong><span>{date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit' })}</span></div>;
};

const getOrderDropBlockReason = (source, target) => {
  if (!source) return 'Choose an order to move';
  if (source.shipmentId === target.id) return 'Choose another shipment';
  if (source.status !== 'pending' || target.status !== 'pending') return 'Only pending shipments can be edited';
  if (Number(source.warehouseId) !== Number(target.warehouseId)) return 'Orders must stay in the same hub';
  if (String(source.shipmentDate) !== String(target.shipmentDate)) return 'Shipment dates must match';
  return '';
};

const Shipments = () => {
  const currentUser = getCurrentUser();
  const isSuperAdmin = currentUser.role === 'superAdmin';
  const canConfigure = isSuperAdmin || currentUser.access?.includes('deliveryAdmin');
  const canManageShipments = canConfigure;
  const canViewAssignments = canManageShipments;
  const isDeliveryUser = !canManageShipments;
  const [shipmentScope, setShipmentScope] = useState(canManageShipments ? 'all' : 'available');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [vehicleType, setVehicleType] = useState('all');
  const [hubId, setHubId] = useState('all');
  const [area, setArea] = useState('all');
  const [shipmentDate, setShipmentDate] = useState(getCurrentShipmentDate);
  const [shipments, setShipments] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [hubs, setHubs] = useState([]);
  const [areas, setAreas] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, perPage: 20, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeShipment, setActiveShipment] = useState(null);
  const [tabCounts, setTabCounts] = useState({ available: null, mine: null });
  const [assignableDriversByHub, setAssignableDriversByHub] = useState({});
  const [assignmentSaving, setAssignmentSaving] = useState({});
  const [vehicleTypeSaving, setVehicleTypeSaving] = useState({});
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [orderMoveSaving, setOrderMoveSaving] = useState(null);

  const activeFilterCount = [shipmentDate, search.trim(), hubId !== 'all', area !== 'all', vehicleType !== 'all', shipmentScope !== 'available' && status !== 'all']
    .filter(Boolean).length;

  const clearFilters = () => {
    setShipmentDate('');
    setSearch('');
    setHubId('all');
    setArea('all');
    setVehicleType('all');
    setStatus('all');
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPagination(current => ({ ...current, page: 1 }));
  }, [debouncedSearch, status, vehicleType, hubId, area, shipmentDate, shipmentScope]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getShipments({
      page: pagination.page,
      perPage: pagination.perPage,
      search: debouncedSearch,
      status,
      vehicleType,
      hubId,
      area,
      date: shipmentDate,
      scope: shipmentScope,
    }).then(response => {
      if (cancelled) return;
      const data = response.data || {};
      setShipments(data.shipments || []);
      setSummary(data.summary || EMPTY_SUMMARY);
      setHubs(data.hubs || []);
      setAreas(data.areas || []);
      setActiveShipment(data.activeShipment || null);
      setAssignableDriversByHub(data.assignableDriversByHub || {});
      setPagination(current => ({ ...current, total: data.pagination?.total || 0 }));
      if (shipmentScope === 'available' || shipmentScope === 'mine') {
        setTabCounts(current => ({ ...current, [shipmentScope]: Number(data.pagination?.total || 0) }));
      }
    }).catch(requestError => {
      if (!cancelled) setError(requestError.message || 'Could not load shipments');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [area, debouncedSearch, hubId, pagination.page, pagination.perPage, shipmentDate, shipmentScope, status, vehicleType]);

  const changeShipmentScope = (nextScope) => {
    setShipmentScope(nextScope);
    setStatus('all');
    setArea('all');
    setFiltersOpen(false);
  };

  const handleScopeKeyDown = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextScope = ['ArrowRight', 'End'].includes(event.key) ? 'mine' : 'available';
    changeShipmentScope(nextScope);
    window.requestAnimationFrame(() => document.getElementById(`shipment-tab-${nextScope}`)?.focus());
  };

  const handleDriverAssignment = useCallback(async (shipment, driverId) => {
    const normalizedDriverId = driverId ? String(driverId) : '';
    if (normalizedDriverId === String(shipment.assignedTo?.id || '')) return true;
    setAssignmentSaving(current => ({ ...current, [shipment.id]: true }));
    setError('');
    try {
      const response = await assignShipmentDriver(shipment.id, normalizedDriverId || null);
      setShipments(current => current.map(item => item.id === shipment.id
        ? { ...item, assignedTo: response.data?.assignedTo || null, assignedAt: response.data?.assignedAt || null }
        : item));
      return true;
    } catch (requestError) {
      const message = requestError.message || 'Could not update the shipment assignment';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setAssignmentSaving(current => {
        const next = { ...current };
        delete next[shipment.id];
        return next;
      });
    }
  }, []);

  const handleVehicleTypeChange = useCallback(async (shipment, vehicleTypeValue) => {
    if (!vehicleTypeValue || vehicleTypeValue === shipment.vehicleType) return true;
    setVehicleTypeSaving(current => ({ ...current, [shipment.id]: true }));
    setError('');
    try {
      const response = await updateShipmentVehicleType(shipment.id, vehicleTypeValue);
      setShipments(current => current.map(item => item.id === shipment.id
        ? {
          ...item,
          vehicleType: response.data?.vehicleType || vehicleTypeValue,
          canEditVehicleType: response.data?.canEditVehicleType,
          vehicleTypeAdvisory: response.data?.vehicleTypeAdvisory,
        }
        : item));
      return true;
    } catch (requestError) {
      const message = requestError.message || 'Could not update the shipment vehicle type';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setVehicleTypeSaving(current => {
        const next = { ...current };
        delete next[shipment.id];
        return next;
      });
    }
  }, []);

  const handleOrderDragStart = useCallback((event, shipment, orderId) => {
    const payload = {
      orderId,
      shipmentId: shipment.id,
      warehouseId: shipment.warehouseId,
      shipmentDate: shipment.shipmentDate,
      status: shipment.status,
    };
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/x-oms-shipment-order', JSON.stringify(payload));
    event.dataTransfer.setData('text/plain', orderId);
    setDraggedOrder(payload);
    setDropTargetId(null);
  }, []);

  const handleOrderDragEnd = useCallback(() => {
    setDraggedOrder(null);
    setDropTargetId(null);
  }, []);

  const handleOrderDrop = useCallback(async (event, targetShipment) => {
    event.preventDefault();
    const source = draggedOrder;
    setDropTargetId(null);
    if (!source || orderMoveSaving) return;

    const blockedReason = getOrderDropBlockReason(source, targetShipment);
    if (blockedReason) {
      toast.warning(blockedReason);
      return;
    }

    setOrderMoveSaving({ orderId: source.orderId, sourceId: source.shipmentId, targetId: targetShipment.id });
    setError('');
    try {
      const response = await moveShipmentOrder(source.shipmentId, source.orderId, targetShipment.id);
      const result = response.data || {};
      setShipments(current => current
        .filter(item => !(result.sourceDeleted && item.id === source.shipmentId))
        .map(item => {
          if (item.id === source.shipmentId && result.source) return { ...item, ...result.source };
          if (item.id === targetShipment.id && result.target) return { ...item, ...result.target };
          return item;
        }));
      if (result.sourceDeleted) {
        setPagination(current => ({ ...current, total: Math.max(0, current.total - 1) }));
        setSummary(current => ({
          ...current,
          shipments: Math.max(0, current.shipments - 1),
          pending: Math.max(0, current.pending - 1),
        }));
      }
      toast.success(`${source.orderId} moved to ${targetShipment.id}. Both routes are updated.`);
    } catch (requestError) {
      const message = requestError.message || 'Could not move the order';
      setError(message);
      toast.error(message);
    } finally {
      setOrderMoveSaving(null);
      setDraggedOrder(null);
    }
  }, [draggedOrder, orderMoveSaving]);

  const columns = useMemo(() => {
    const isAvailable = shipmentScope === 'available';
    const widths = { orders: '190px', shipment: '130px', date: '110px', type: '160px', load: '170px', utilization: '140px', vehicle: '120px', started: '130px', status: '130px', action: '96px' };
    return [
    { id: 'orders', name: 'Orders', selector: row => row.orderCount, sortable: true, width: widths.orders, style: { overflow: 'visible' }, cell: row => {
      const dropReason = draggedOrder ? getOrderDropBlockReason(draggedOrder, row) : '';
      return <ShipmentOrdersCell shipment={row} canOpenOrders={canManageShipments} dragDrop={canManageShipments ? {
        enabled: row.status === 'pending' && !orderMoveSaving,
        active: Boolean(draggedOrder),
        activeOrderId: draggedOrder?.orderId,
        isSource: draggedOrder?.shipmentId === row.id,
        isTarget: dropTargetId === row.id,
        canDrop: !dropReason,
        dropReason,
        moving: orderMoveSaving?.sourceId === row.id || orderMoveSaving?.targetId === row.id,
        onDragStart: handleOrderDragStart,
        onDragEnd: handleOrderDragEnd,
        onTargetChange: setDropTargetId,
        onDrop: handleOrderDrop,
      } : null} />;
    } },
    {
      id: 'shipment', name: 'Shipment', selector: row => row.id, sortable: true, width: widths.shipment,
      cell: row => <div className="shipment-id-cell"><Link to={`/shipments/${row.id}`} target="_blank" rel="noopener noreferrer" title={row.id}>{row.id}</Link><span title={row.hub}>{row.hub}</span></div>,
    },
    { id: 'shipmentDate', name: 'Date', selector: row => row.shipmentDate, sortable: true, width: widths.date, cell: row => <span className="shipment-date-cell">{formatDate(row.shipmentDate)}</span> },
    {
      id: 'area', name: 'Delivery area', selector: row => row.area, sortable: true, minWidth: '150px', grow: 1,
      cell: row => { const pincodes = (row.pincodes || []).join(', '); return <div className="shipment-area-cell" title={[row.area, pincodes].filter(Boolean).join(' · ')}><strong>{row.area || '—'}</strong><span>{pincodes || '—'}</span></div>; },
    },
    {
      id: 'assignedTo', name: 'Driver', selector: row => row.assignedTo?.name || row.assignedTo?.email || '', sortable: true, minWidth: '220px', grow: 1,
      cell: row => <ShipmentDriverCell shipment={row} drivers={assignableDriversByHub[row.warehouseId] || []} saving={Boolean(assignmentSaving[row.id])} onChange={handleDriverAssignment} editable={canManageShipments} />,
    },
    { id: 'vehicleType', name: 'Vehicle type', selector: row => row.vehicleType, sortable: true, width: widths.type, cell: row => <ShipmentVehicleTypeCell shipment={row} saving={Boolean(vehicleTypeSaving[row.id])} editable={Boolean(canManageShipments && row.canEditVehicleType)} onChange={handleVehicleTypeChange} /> },
    {
      id: 'load', name: 'Product load', width: widths.load, style: { overflow: 'visible' },
      cell: row => <ShipmentProductLoad shipment={row} />,
    },
    {
      id: 'utilization', name: 'Utilization', selector: row => row.loadPoints / row.capacityPoints, sortable: true, width: widths.utilization,
      cell: row => <ShipmentUtilization shipment={row} />,
    },
    ...(!isAvailable ? [
      { id: 'vehicle', name: 'Vehicle', selector: row => row.vehicle || '', sortable: true, width: widths.vehicle, cell: row => row.vehicle ? <span className="shipment-vehicle-number" title={row.vehicle}>{row.vehicle}</span> : <span className="shipment-not-started">—</span> },
      { id: 'startedAt', name: 'Started at', selector: row => row.startedAt || '', sortable: true, width: widths.started, cell: row => <ShipmentStartedAt value={row.startedAt} /> },
    ] : []),
    { id: 'status', name: 'Status', selector: row => row.status, sortable: true, width: widths.status, cell: row => <span className={`shipment-status shipment-status-${row.status}`}>{SHIPMENT_STATUS_LABELS[row.status] || STATUS_LABELS[row.status]}</span> },
    { id: 'action', name: 'Action', width: widths.action, right: true, button: true, cell: row => <Link className="shipment-row-action" to={`/shipments/${row.id}`} target="_blank" rel="noopener noreferrer" title={`View ${row.id}`} aria-label={`View shipment ${row.id} in a new tab`}><span>View</span><ViewIcon /></Link> },
  ];
  }, [assignableDriversByHub, assignmentSaving, canManageShipments, draggedOrder, dropTargetId, handleDriverAssignment, handleOrderDragEnd, handleOrderDragStart, handleOrderDrop, handleVehicleTypeChange, orderMoveSaving, shipmentScope, vehicleTypeSaving]);

  const tableStyles = useMemo(() => ({
    table: { style: { tableLayout: 'fixed', width: '100%', minWidth: '1480px' } },
    headRow: { style: { minHeight: '44px', height: '44px', position: 'sticky', top: 0, zIndex: 4, borderBottom: '1px solid #eef0f3', backgroundColor: '#f8fafc' } },
    headCells: { style: { minWidth: 0, padding: '0 14px', color: '#697386', fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'visible' } },
    cells: { style: { minWidth: 0, padding: '12px 14px', color: '#344054', fontSize: 11, overflow: 'hidden', alignItems: 'center' } },
    rows: { style: { minHeight: 0, height: 'auto', maxHeight: 'none', borderBottom: '1px solid #eef0f3', backgroundColor: '#fff', '&:hover': { backgroundColor: '#f8fafc' } } },
    pagination: { style: { minHeight: '54px', padding: '0 12px', borderTop: '1px solid #e7eaf0', color: '#596579', fontSize: 11 } },
  }), []);

  return (
    <main className="shipments-page">
      <header className="shipments-page-header">
        <div><h1 className="page-title">Shipments</h1><p className="page-subtitle">Area-wise vehicle loads generated from ready orders</p></div>
        <div className="shipment-header-actions">
          {canManageShipments && <Link className="btn btn-primary" to="/shipments-dashboard">Dashboard</Link>}
          {canConfigure && <Link className="btn btn-secondary" to="/shipments-config">Vehicle Configuration</Link>}
          {isSuperAdmin && <span className="shipments-run-note">Shipment planning · Daily at 8:00 AM IST</span>}
        </div>
      </header>

      {isDeliveryUser && <div className="shipment-scope-tabs" role="tablist" aria-label="Shipment views">
        <button type="button" role="tab" id="shipment-tab-available" aria-selected={shipmentScope === 'available'} tabIndex={shipmentScope === 'available' ? 0 : -1} className={shipmentScope === 'available' ? 'is-active' : ''} onClick={() => changeShipmentScope('available')} onKeyDown={handleScopeKeyDown}>Assigned to me <span>{tabCounts.available ?? (shipmentScope === 'available' ? pagination.total : '—')}</span><small>Pending rides ready to start</small></button>
        <button type="button" role="tab" id="shipment-tab-mine" aria-selected={shipmentScope === 'mine'} tabIndex={shipmentScope === 'mine' ? 0 : -1} className={shipmentScope === 'mine' ? 'is-active' : ''} onClick={() => changeShipmentScope('mine')} onKeyDown={handleScopeKeyDown}>My shipments <span>{tabCounts.mine ?? (shipmentScope === 'mine' ? pagination.total : '—')}</span><small>Ongoing and completed</small></button>
      </div>}

      {isDeliveryUser && activeShipment && <div className="shipment-active-trip-banner">
        <div className="shipment-active-trip-icon"><i /></div>
        <div><span>Active shipment</span><strong>{activeShipment.id}</strong><small>{activeShipment.hub} · Started {formatDateTime(activeShipment.startedAt)}</small></div>
        <p>Complete this shipment before starting another one.</p>
        <Link to={`/shipments/${activeShipment.id}`} target="_blank" rel="noopener noreferrer">Continue shipment <span aria-hidden="true">→</span></Link>
      </div>}

      <div className={`shipment-summary-grid ${isDeliveryUser ? 'shipment-summary-driver' : ''}`}>
        {canManageShipments ? <>
          <SummaryCard label="Total shipments" value={summary.shipments} tone="blue" />
          <SummaryCard label="Orders allocated" value={summary.orders} tone="violet" />
          <SummaryCard label="Pending" value={summary.pending} tone="amber" />
          <SummaryCard label="Started" value={summary.started} tone="blue" />
          <SummaryCard label="Completed" value={summary.completed} tone="green" />
        </> : shipmentScope === 'available' ? <>
          <SummaryCard label="Assigned shipments" value={summary.shipments} tone="blue" />
          <SummaryCard label="Orders ready" value={summary.orders} tone="violet" />
          <SummaryCard label="Ready to start" value={summary.pending} tone="amber" />
        </> : <>
          <SummaryCard label="My shipments" value={summary.shipments} tone="violet" />
          <SummaryCard label="Orders handled" value={summary.orders} tone="blue" />
          <SummaryCard label="Ongoing" value={summary.started} tone="amber" />
          <SummaryCard label="Completed" value={summary.completed} tone="green" />
        </>}
      </div>

      <div className="card shipments-list-card">
        <div className="shipments-toolbar">
          <div className="shipments-toolbar-title"><div><h2>{shipmentScope === 'available' ? 'My assigned shipment queue' : shipmentScope === 'mine' ? 'My shipment history' : 'Shipment list'}</h2><p>{pagination.total} shipment{pagination.total === 1 ? '' : 's'}</p></div><button type="button" className="shipment-mobile-filter-toggle" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(value => !value)}>Filters{activeFilterCount > 0 && <span>{activeFilterCount}</span>}</button></div>
          <div className={`shipments-filters ${filtersOpen ? 'is-open' : ''}`}>
            <label className="shipment-filter-field shipment-search-filter" htmlFor="shipment-search"><span>Search</span><div className="shipment-search-wrap"><SearchIcon /><input id="shipment-search" type="search" className="form-control" value={search} onChange={event => setSearch(event.target.value)} placeholder="Shipment, area or vehicle…" /></div></label>
            <label className="shipment-filter-field" htmlFor="shipment-date"><span>Shipment date</span><input id="shipment-date" type="date" className="form-control shipment-styled-date" value={shipmentDate} onChange={event => setShipmentDate(event.target.value)} /></label>
            <label className="shipment-filter-field" htmlFor="shipment-hub"><span>Hub</span><select id="shipment-hub" className="form-control" value={hubId} onChange={event => { setHubId(event.target.value); setArea('all'); }}><option value="all">All hubs</option>{hubs.map(hub => <option key={hub.id} value={hub.id}>{hub.name}</option>)}</select></label>
            <label className="shipment-filter-field" htmlFor="shipment-area"><span>Area</span><select id="shipment-area" className="form-control" value={area} onChange={event => setArea(event.target.value)}><option value="all">All areas</option>{areas.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
            <label className="shipment-filter-field" htmlFor="shipment-vehicle-type"><span>Vehicle type</span><select id="shipment-vehicle-type" className="form-control" value={vehicleType} onChange={event => setVehicleType(event.target.value)}><option value="all">All vehicle types</option>{Object.entries(VEHICLE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            {shipmentScope !== 'available' && <label className="shipment-filter-field" htmlFor="shipment-status"><span>Status</span><select id="shipment-status" className="form-control" value={status} onChange={event => setStatus(event.target.value)}><option value="all">{shipmentScope === 'mine' ? 'All activity' : 'All statuses'}</option>{Object.entries(STATUS_LABELS).filter(([value]) => shipmentScope !== 'mine' || value !== 'pending').map(([value, label]) => <option key={value} value={value}>{value === 'started' && shipmentScope === 'mine' ? 'Ongoing' : label}</option>)}</select></label>}
            {activeFilterCount > 0 && <button type="button" className="shipment-clear-filters" onClick={clearFilters}>Clear all filters</button>}
          </div>
        </div>
        {error && <div className="shipment-api-error">{error}</div>}
        <div className={`shipment-table-scroll ${shipmentScope === 'available' ? 'is-available' : 'has-trip-columns'}`}><div className="shipment-table-inner">
        <DataTable key={`shipments-${shipmentScope}-${canViewAssignments ? 'admin' : 'delivery'}`} className="shipments-data-table"
          columns={columns}
          data={shipments}
          progressPending={loading}
          progressComponent={<ShipmentTableSkeleton />}
          pagination
          paginationServer
          paginationTotalRows={pagination.total}
          paginationDefaultPage={pagination.page}
          paginationResetDefaultPage={false}
          paginationPerPage={pagination.perPage}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          paginationComponentOptions={{ rowsPerPageText: 'Rows per page', rangeSeparatorText: 'of', selectAllRowsItem: false }}
          onChangePage={page => setPagination(current => ({ ...current, page }))}
          onChangeRowsPerPage={(perPage, page) => setPagination(current => ({ ...current, perPage, page }))}
          customStyles={tableStyles}
          highlightOnHover striped responsive
          noDataComponent={<ShipmentEmptyState scope={shipmentScope} hasFilters={activeFilterCount > 0} onClear={clearFilters} />}
          mobileSummaryFields={['Shipment', 'Shipment date', 'Delivery area', 'Assigned driver', 'Vehicle type', 'Product load', 'Utilization', 'Status']}
          mobileCardRenderer={row => <ShipmentMobileCard shipment={row} showAssignee canManage={canManageShipments} drivers={assignableDriversByHub[row.warehouseId] || []} assignmentSaving={Boolean(assignmentSaving[row.id])} vehicleTypeSaving={Boolean(vehicleTypeSaving[row.id])} onAssign={handleDriverAssignment} onVehicleTypeChange={handleVehicleTypeChange} />}
        />
        </div></div>
      </div>
    </main>
  );
};

const SummaryCard = ({ label, value, tone }) => <div className={`shipment-summary-card shipment-summary-${tone}`}><span>{label}</span><strong>{value}</strong></div>;

const ShipmentProductLoad = ({ shipment }) => {
  const products = [
    ['panels', 'P', 'Panels', Number(shipment.panels || 0)],
    ['inverters', 'I', 'Inverters', Number(shipment.inverters || 0)],
    ['batteries', 'B', 'Batteries', Number(shipment.batteries || 0)],
    ['bos', 'BOS', 'BOS', Number(shipment.bos || 0)],
  ];
  const visible = products.filter(([, , , count]) => count > 0);
  const title = products.map(([, , label, count]) => `${count} ${label}`).join(' · ');
  if (!visible.length) return <span className="shipment-empty-value">—</span>;
  return <div className="shipment-product-load shipment-product-load-compact" title={title}>{visible.map(([type, short, label, count]) => <span className={`is-${type}`} key={type}><b>{short}</b><strong>{count}</strong><em>{label}</em></span>)}</div>;
};

const ShipmentUtilization = ({ shipment }) => {
  const percent = shipment.capacityPoints ? Math.round((shipment.loadPoints / shipment.capacityPoints) * 100) : 0;
  const overTarget = percent > 100;
  const tone = overTarget ? 'over' : percent >= 70 ? 'healthy' : 'neutral';
  const loadLabel = `${shipment.loadPoints}/${shipment.capacityPoints} pts`;
  return <div className={`shipment-utilization is-${tone}`} title={`${shipment.loadPoints} / ${shipment.capacityPoints} planning points`}><div><strong>{percent}%</strong><span>{loadLabel}</span>{overTarget && <b title="Above planning target">!</b>}</div><div className="shipment-utilization-track"><span style={{ width: `${Math.min(percent, 100)}%` }} /></div>{overTarget && <small>Above target</small>}</div>;
};

const ShipmentTableSkeleton = () => <div className="shipment-table-skeleton" aria-label="Loading shipments">{Array.from({ length: 6 }, (_, index) => <div key={index}>{Array.from({ length: 7 }, (__, cell) => <span key={cell} />)}</div>)}</div>;

const ShipmentEmptyState = ({ scope, hasFilters, onClear }) => <div className="shipments-empty"><span aria-hidden="true">▦</span><strong>{hasFilters ? 'No shipments match your filters' : scope === 'available' ? 'No shipments are assigned to you' : scope === 'mine' ? 'No shipment activity yet' : 'No shipments found'}</strong><p>{hasFilters ? 'Try changing or clearing the active filters.' : scope === 'available' ? 'A Delivery Admin can assign a pending shipment from your hub.' : 'Shipments you start will appear here.'}</p>{hasFilters && <button type="button" onClick={onClear}>Clear filters</button>}</div>;

const ShipmentMobileCard = ({ shipment, showAssignee, canManage, drivers, assignmentSaving, vehicleTypeSaving, onAssign, onVehicleTypeChange }) => {
  return <div className="shipment-mobile-card-content">
    <div className="shipment-mobile-card-header"><div><Link to={`/shipments/${shipment.id}`} target="_blank" rel="noopener noreferrer">{shipment.id}</Link><span>{shipment.hub}</span></div><span className={`shipment-status shipment-status-${shipment.status}`}>{SHIPMENT_STATUS_LABELS[shipment.status] || STATUS_LABELS[shipment.status]}</span></div>
    <div className="shipment-mobile-card-main"><div><span>Delivery area</span><strong>{shipment.area}</strong><small>{(shipment.pincodes || []).join(', ')}</small></div><div><span>Shipment date</span><strong>{formatDate(shipment.shipmentDate)}</strong></div></div>
    <ShipmentOrdersCell shipment={shipment} mobile canOpenOrders={canManage} />
    {showAssignee && <ShipmentDriverCell shipment={shipment} drivers={drivers} saving={assignmentSaving} onChange={onAssign} editable={canManage} mobile />}
    <ShipmentProductLoad shipment={shipment} />
    <div className="shipment-mobile-metrics"><div><ShipmentVehicleTypeCell shipment={shipment} saving={vehicleTypeSaving} editable={Boolean(canManage && shipment.canEditVehicleType)} onChange={onVehicleTypeChange} mobile /></div><div><span>Orders</span><strong>{shipment.orderCount}</strong></div><ShipmentUtilization shipment={shipment} /></div>
    <Link className="shipment-mobile-open" to={`/shipments/${shipment.id}`} target="_blank" rel="noopener noreferrer">View shipment <span aria-hidden="true">→</span></Link>
  </div>;
};

export default Shipments;
